import React from 'react';
import { Globe, ChevronDown, ChevronRight, Check, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

/**
 * AuthorSignatureCard - Configuration de la signature auteur WordPress
 */
const AuthorSignatureCard = ({
  sites,
  showCredentials,
  setShowCredentials,
  authorEdits,
  authorMappings,
  savingCreds,
  onUpdateAuthorEdit,
  onPickAuthorCandidate,
  onSaveCredentials,
}) => {
  return (
    <Card className="relative bg-slate-950/40 backdrop-blur-xl border-slate-800/60">
      <CardHeader
        className="cursor-pointer select-none hover:bg-slate-900/30 transition-colors rounded-t-lg"
        onClick={() => setShowCredentials(!showCredentials)}
        data-testid="toggle-credentials"
      >
        <CardTitle className="text-xl md:text-2xl font-orbitron uppercase tracking-wider text-cyan-400 flex items-center justify-between">
          <span className="flex items-center">
            <Globe className="w-5 h-5 md:w-6 md:h-6 mr-2 md:mr-3" />
            Ma signature WordPress
          </span>
          {showCredentials ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
        </CardTitle>
      </CardHeader>
      {showCredentials && (
      <CardContent>
        <div className="text-sm text-slate-500 mb-4 space-y-1">
          <p>Configurez votre <strong>signature d&apos;auteur</strong> pour chaque site WordPress.</p>
          <p className="text-xs">Le nom, slug et ID servent à attribuer l&apos;article à votre compte auteur sur le site.</p>
        </div>

        {sites.length === 0 ? (
          <p className="text-slate-400 text-center py-4">Aucun site configuré</p>
        ) : (
          <div className="space-y-4">
            {sites.map((site) => {
              const author = authorEdits[site.id] || { query: '' };
              const mapping = authorMappings[site.id];
              const hasAuthor = !!mapping;
              return (
                <div key={site.id} className="p-4 bg-slate-900/50 rounded-lg border border-slate-800" data-testid={`site-author-${site.id}`}>
                  <div className="flex items-center mb-3 flex-wrap gap-2">
                    <div className="w-8 h-8 rounded bg-slate-800 flex items-center justify-center overflow-hidden">
                      <img
                        src={`https://www.google.com/s2/favicons?domain=${site.url}&sz=32`}
                        alt=""
                        className="w-5 h-5"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-cyan-400 font-semibold truncate">{site.name}</p>
                      <p className="text-xs text-slate-500 truncate">{site.url}</p>
                    </div>
                    {hasAuthor ? (
                      <span className="text-[10px] text-cyan-400 bg-cyan-500/10 px-2 py-1 rounded border border-cyan-500/30">
                        ✓ {mapping.wp_author_name}
                      </span>
                    ) : (
                      <span className="text-[10px] text-amber-400 bg-amber-500/10 px-2 py-1 rounded border border-amber-500/30">
                        Non configuré
                      </span>
                    )}
                  </div>

                  <div className="mb-3">
                    <label className="text-[10px] text-slate-500">Nom, slug ou ID WordPress</label>
                    <Input
                      type="text"
                      value={author.query || ''}
                      onChange={(e) => onUpdateAuthorEdit(site.id, 'query', e.target.value)}
                      placeholder="Jean Dupont, jean-dupont ou 123"
                      className="mt-1 bg-slate-950/50 border-slate-700 text-slate-100 text-sm"
                      data-testid={`author-query-${site.id}`}
                    />
                    {mapping?.wp_author_id && (
                      <div className="mt-2 text-[11px] text-emerald-400 flex items-center gap-2 flex-wrap">
                        <Check className="w-3 h-3" />
                        <span>Résolu : <strong>{mapping.wp_author_name}</strong></span>
                        <span className="text-cyan-400 font-mono bg-cyan-500/10 px-1.5 py-0.5 rounded border border-cyan-500/30">ID #{mapping.wp_author_id}</span>
                        {mapping.wp_author_slug && <span className="text-slate-500 font-mono">/{mapping.wp_author_slug}</span>}
                      </div>
                    )}
                    {author.candidates && author.candidates.length > 0 && (
                      <div className="mt-2 p-2 rounded border border-amber-500/40 bg-amber-500/5">
                        <p className="text-[11px] text-amber-300 mb-1.5">Plusieurs auteurs correspondent :</p>
                        <div className="space-y-1">
                          {author.candidates.map((c) => (
                            <button
                              key={c.id}
                              type="button"
                              onClick={() => onPickAuthorCandidate(site.id, c)}
                              className="w-full text-left text-xs p-1.5 rounded bg-slate-800/60 hover:bg-cyan-500/10 border border-transparent hover:border-cyan-500/40"
                              data-testid={`author-candidate-${site.id}-${c.id}`}
                            >
                              <span className="text-cyan-400 font-mono">#{c.id}</span>
                              <span className="text-slate-200 ml-2">{c.name}</span>
                              {c.slug && <span className="text-slate-500 font-mono ml-1">/{c.slug}</span>}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {author.error && !author.candidates?.length && (
                      <p className="text-[11px] text-red-400 mt-1">{author.error}</p>
                    )}
                  </div>

                  <Button
                    onClick={() => onSaveCredentials(site.id)}
                    disabled={savingCreds[site.id]}
                    size="sm"
                    className="bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 border border-cyan-500/50"
                    data-testid={`save-author-${site.id}`}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {savingCreds[site.id] ? 'Sauvegarde...' : 'Enregistrer'}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
      )}
    </Card>
  );
};

export default AuthorSignatureCard;
